import { useEffect, useState, type FC } from 'react';

import { Button, Modal } from '@skillarea/lib-frontend';
import { useTranslation } from 'react-i18next';

import cx from 'classnames';

import type { IGameComponentProps } from '../registry';

import styles from './DemoPlayModal.module.scss';

interface IDemoPlayModalProps {
  gameSlug: string;
  isOpen: boolean;
  onClose: () => void;
  GameComponent: FC<IGameComponentProps>;
  opponentName?: string;
  playerName?: string;
  rules: string;
  showTestPromotionButton?: boolean;
  showTestSituationButtons?: boolean;
}

export const DemoPlayModal: FC<IDemoPlayModalProps> = ({
  GameComponent,
  gameSlug,
  isOpen,
  onClose,
  opponentName,
  playerName,
  rules,
  showTestPromotionButton,
  showTestSituationButtons,
}) => {
  const { t } = useTranslation('games');
  const resolvedPlayerName = playerName ?? t('ui.demo.player');
  const resolvedOpponentName = opponentName ?? t('ui.demo.opponent');
  const [demoDifficultyDraft, setDemoDifficultyDraft] = useState(0.5);
  const [demoDifficultyApplied, setDemoDifficultyApplied] = useState(0.5);
  const [demoRestartNonce, setDemoRestartNonce] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    setDemoDifficultyDraft(demoDifficultyApplied);
  }, [demoDifficultyApplied, isOpen]);

  return (
    <Modal
      classNameModal="sm:px-0"
      isOpen={isOpen}
      onBeforeClose={onClose}
      preventOutsideClose
      size="fullwidth"
      title={t('ui.demo.title')}
    >
      <div className={styles.demoPlayWrapper}>
        <div className={styles.demoControls}>
          <div className={styles.demoControlsLabel}>
            {t('ui.demo.difficulty', {
              percent: (demoDifficultyDraft * 100).toFixed(0),
            })}
          </div>
          <div className={styles.demoControlsRow}>
            <input
              aria-label={t('ui.demo.difficultyAria')}
              className={styles.demoControlsSlider}
              max={1}
              min={0}
              onChange={(e) => {
                const value = Number(e.target.value);
                setDemoDifficultyDraft(Number.isFinite(value) ? value : 0.5);
              }}
              step={0.05}
              type="range"
              value={demoDifficultyDraft}
            />
            <Button
              className={cx(
                styles.demoControlsApply,
                demoDifficultyDraft === demoDifficultyApplied &&
                  styles.demoControlsApplyDisabled,
              )}
              color="accent"
              disabled={demoDifficultyDraft === demoDifficultyApplied}
              onClick={() => {
                setDemoDifficultyApplied(demoDifficultyDraft);
                setDemoRestartNonce((v) => v + 1);
              }}
              size="s"
              variant="filled"
            >
              {t('ui.demo.apply')}
            </Button>
          </div>
        </div>
        <GameComponent
          aiDifficulty={demoDifficultyApplied}
          key={`${gameSlug}-${demoRestartNonce}`}
          onSurrender={onClose}
          player1={{ name: resolvedPlayerName }}
          player2={{ name: resolvedOpponentName }}
          rules={rules}
          showTestPromotionButton={showTestPromotionButton}
          showTestSituationButtons={showTestSituationButtons}
        />
      </div>
    </Modal>
  );
};
